import type { ButtonProps as MuiButtonProps } from '@mui/material';
import { Button as MuiButton } from '@mui/material';
import classNames from 'classnames/bind';
import React from 'react';

import styles from './Button.module.scss';

const cx = classNames.bind(styles);

/** Semantic intent, resolved to token colors rather than MUI palette keys. */
export type ButtonTone = 'brand' | 'neutral' | 'danger' | 'plain';

export type ButtonProps = Omit<
  MuiButtonProps,
  'variant' | 'color' | 'size' | 'startIcon' | 'endIcon'
> & {
  tone?: ButtonTone;
  /** Outlined rather than filled. */
  outline?: boolean;
  /** No fill or border — MUI's `text` variant. Wins over `outline`. */
  quiet?: boolean;
  size?: 'sm' | 'md' | 'lg';
  shape?: 'rounded' | 'pill' | 'square';
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  /** Defaults to `'button'` so a Button inside a form never submits by accident. */
  type?: 'button' | 'submit' | 'reset';
};

const VARIANT_PREFIX = {
  solid: 'solid',
  outline: 'outline',
  quiet: 'quiet',
} as const;

const TONE_SUFFIX: Record<ButtonTone, string> = {
  brand: 'Brand',
  neutral: 'Neutral',
  danger: 'Danger',
  plain: 'Plain',
};

/**
 * The app's button.
 *
 * Wraps MUI `Button` rather than a raw `<button>` so ripple, focus-visible and
 * disabled semantics come for free, and so the theme stays meaningful. Tone and
 * variant are separate props because the eight button idioms this replaces varied
 * on both axes independently.
 *
 * Icon-only buttons are a different control with a different a11y contract —
 * use `IconButton`, which requires a `label`.
 */
function Button({
  tone = 'brand',
  outline = false,
  quiet = false,
  size = 'md',
  shape = 'rounded',
  icon,
  iconPosition = 'start',
  type = 'button',
  fullWidth = false,
  className,
  children,
  ...rest
}: ButtonProps) {
  const variant = quiet ? 'quiet' : outline ? 'outline' : 'solid';
  const toneClass = `${VARIANT_PREFIX[variant]}${TONE_SUFFIX[tone]}`;
  const classes = cx('root', size, shape, toneClass, { fullWidth }, className);

  return (
    <MuiButton
      className={classes}
      type={type}
      variant={quiet ? 'text' : outline ? 'outlined' : 'contained'}
      disableElevation
      startIcon={icon && iconPosition === 'start' ? icon : undefined}
      endIcon={icon && iconPosition === 'end' ? icon : undefined}
      {...rest}
    >
      {children}
    </MuiButton>
  );
}

Button.displayName = 'Button';
export default Button;
